import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ProjectWorkflowFlow,
  projectWorkflowService,
  ProjectWorkflowTemplate,
  TriggerProjectWorkflowStepNowResponse,
  WorkflowStep,
} from '@services/projectWorkflowService';
import { CACHE_DURATION as CACHE_TTL } from '../utils/cache';

export const projectWorkflowStepTriggerKey = (projectId: string, stepId: string) =>
  `${projectId}:${stepId}`;

interface ProjectWorkflowState {
  byProjectId: Record<string, ProjectWorkflowTemplate>;
  loadingByProjectId: Record<string, boolean>;
  savingByProjectId: Record<string, boolean>;
  activatingFlowByProjectId: Record<string, boolean>;
  triggeringByStepKey: Record<string, boolean>;
  errorByProjectId: Record<string, string | null>;
  lastFetchedByProjectId: Record<string, number>;
}

const initialState: ProjectWorkflowState = {
  byProjectId: {},
  loadingByProjectId: {},
  savingByProjectId: {},
  activatingFlowByProjectId: {},
  triggeringByStepKey: {},
  errorByProjectId: {},
  lastFetchedByProjectId: {},
};

export const fetchProjectWorkflow = createAsyncThunk<
  { projectId: string; template: ProjectWorkflowTemplate },
  { projectId: string; force?: boolean },
  { state: { projectWorkflow: ProjectWorkflowState } }
>('projectWorkflow/fetch', async ({ projectId, force }, { getState, rejectWithValue }) => {
  if (!force) {
    const state = getState().projectWorkflow;
    const last = state.lastFetchedByProjectId[projectId];
    if (last && Date.now() - last < CACHE_TTL && state.byProjectId[projectId]) {
      return { projectId, template: state.byProjectId[projectId] };
    }
  }
  try {
    const template = await projectWorkflowService.getTemplate(projectId);
    return { projectId, template };
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to load workflow');
  }
});

export const saveProjectWorkflow = createAsyncThunk<
  { projectId: string; template: ProjectWorkflowTemplate },
  { projectId: string; steps: WorkflowStep[] }
>('projectWorkflow/save', async ({ projectId, steps }, { rejectWithValue }) => {
  try {
    const template = await projectWorkflowService.updateTemplate(projectId, steps);
    return { projectId, template };
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to save workflow');
  }
});

export const resetProjectWorkflow = createAsyncThunk<
  { projectId: string; template: ProjectWorkflowTemplate },
  { projectId: string }
>('projectWorkflow/reset', async ({ projectId }, { rejectWithValue }) => {
  try {
    const template = await projectWorkflowService.resetTemplate(projectId);
    return { projectId, template };
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to reset workflow');
  }
});

export const deleteProjectWorkflowGroup = createAsyncThunk<
  { projectId: string; template: ProjectWorkflowTemplate; workflowKey: string },
  { projectId: string; workflowKey: string }
>('projectWorkflow/deleteGroup', async ({ projectId, workflowKey }, { rejectWithValue }) => {
  try {
    const template = await projectWorkflowService.deleteWorkflow(projectId, workflowKey);
    return { projectId, template, workflowKey };
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to delete workflow');
  }
});

export const triggerProjectWorkflowStepNow = createAsyncThunk<
  { projectId: string; stepId: string; result: TriggerProjectWorkflowStepNowResponse },
  { projectId: string; stepId: string }
>('projectWorkflow/triggerStepNow', async ({ projectId, stepId }, { rejectWithValue }) => {
  try {
    const result = await projectWorkflowService.triggerStepNow(projectId, stepId);
    return { projectId, stepId, result };
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to trigger workflow step');
  }
});

export const activateProjectWorkflowFlow = createAsyncThunk<
  { projectId: string; flow: ProjectWorkflowFlow },
  { projectId: string; projectName?: string; templateId?: string | null }
>(
  'projectWorkflow/activateFlow',
  async ({ projectId, projectName, templateId }, { rejectWithValue }) => {
    try {
      const flow = await projectWorkflowService.startFlow({
        projectId,
        projectName,
        templateId,
        source: 'manual',
      });
      return { projectId, flow };
    } catch (err) {
      return rejectWithValue((err as Error).message || 'Failed to activate workflow flow');
    }
  }
);

const projectWorkflowSlice = createSlice({
  name: 'projectWorkflow',
  initialState,
  reducers: {
    // Optimistic local update so DnD/edits feel instant; followed by saveProjectWorkflow
    setLocalSteps(state, action: PayloadAction<{ projectId: string; steps: WorkflowStep[] }>) {
      const { projectId, steps } = action.payload;
      const existing = state.byProjectId[projectId];
      state.byProjectId[projectId] = {
        steps,
        isCustomized: existing?.isCustomized ?? false,
        templateId: existing?.templateId ?? null,
        updatedAt: existing?.updatedAt ?? null,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchProjectWorkflow.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        state.loadingByProjectId[projectId] = true;
        state.errorByProjectId[projectId] = null;
      })
      .addCase(fetchProjectWorkflow.fulfilled, (state, action) => {
        const { projectId, template } = action.payload;
        state.byProjectId[projectId] = template;
        state.loadingByProjectId[projectId] = false;
        state.lastFetchedByProjectId[projectId] = Date.now();
      })
      .addCase(fetchProjectWorkflow.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        state.loadingByProjectId[projectId] = false;
        state.errorByProjectId[projectId] =
          (action.payload as string) || action.error.message || 'Failed';
      })
      .addCase(saveProjectWorkflow.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        state.savingByProjectId[projectId] = true;
        state.errorByProjectId[projectId] = null;
      })
      .addCase(saveProjectWorkflow.fulfilled, (state, action) => {
        const { projectId, template } = action.payload;
        state.byProjectId[projectId] = template;
        state.savingByProjectId[projectId] = false;
        state.lastFetchedByProjectId[projectId] = Date.now();
      })
      .addCase(saveProjectWorkflow.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        state.savingByProjectId[projectId] = false;
        state.errorByProjectId[projectId] =
          (action.payload as string) || action.error.message || 'Failed';
      })
      .addCase(deleteProjectWorkflowGroup.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        state.savingByProjectId[projectId] = true;
        state.errorByProjectId[projectId] = null;
      })
      .addCase(deleteProjectWorkflowGroup.fulfilled, (state, action) => {
        const { projectId, template } = action.payload;
        state.byProjectId[projectId] = template;
        state.savingByProjectId[projectId] = false;
        state.lastFetchedByProjectId[projectId] = Date.now();
      })
      .addCase(deleteProjectWorkflowGroup.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        state.savingByProjectId[projectId] = false;
        state.errorByProjectId[projectId] =
          (action.payload as string) || action.error.message || 'Failed';
      })
      .addCase(resetProjectWorkflow.fulfilled, (state, action) => {
        const { projectId, template } = action.payload;
        state.byProjectId[projectId] = template;
        state.lastFetchedByProjectId[projectId] = Date.now();
      })
      .addCase(triggerProjectWorkflowStepNow.pending, (state, action) => {
        const { projectId, stepId } = action.meta.arg;
        const key = projectWorkflowStepTriggerKey(projectId, stepId);
        state.triggeringByStepKey[key] = true;
      })
      .addCase(triggerProjectWorkflowStepNow.fulfilled, (state, action) => {
        const { projectId, stepId } = action.payload;
        const key = projectWorkflowStepTriggerKey(projectId, stepId);
        state.triggeringByStepKey[key] = false;
      })
      .addCase(triggerProjectWorkflowStepNow.rejected, (state, action) => {
        const { projectId, stepId } = action.meta.arg;
        const key = projectWorkflowStepTriggerKey(projectId, stepId);
        state.triggeringByStepKey[key] = false;
      })
      .addCase(activateProjectWorkflowFlow.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        state.activatingFlowByProjectId[projectId] = true;
      })
      .addCase(activateProjectWorkflowFlow.fulfilled, (state, action) => {
        const { projectId } = action.payload;
        state.activatingFlowByProjectId[projectId] = false;
      })
      .addCase(activateProjectWorkflowFlow.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        state.activatingFlowByProjectId[projectId] = false;
      });
  },
});

export const { setLocalSteps } = projectWorkflowSlice.actions;
export default projectWorkflowSlice.reducer;
